// rotateArcAngle — where the pointer is, as an angle around the rotation axis.
//
// This replaces three's rotate mapping, which is why rotating felt
// unresponsive. TransformControls computes:
//
//     ROTATION_SPEED = 20 / distance(gizmo, camera)
//     rotationAngle  = offset · (axis × eye) * ROTATION_SPEED
//
// — a RATE. The angle is proportional to how far you dragged, scaled by an
// inverse-distance constant, so the object never follows the cursor: the same
// gesture rotates a different amount depending on how far the camera happens to
// be, and a small drag on a far object barely moves it.
//
// PlayCanvas tracks the pointer 1:1 instead (engine/src/extras/gizmo/
// rotate-gizmo.js `_calculateArcAngle`, rotationMode 'absolute'): it projects
// the pointer onto the rotation PLANE and takes the angle of that point around
// the gizmo centre. Drag to 3 o'clock and the object points at 3 o'clock. That
// is the model implemented here.
//
// Pure math, no three/DOM imports, so the two things worth pinning are testable
// directly: that a quarter-turn of pointer travel is a quarter-turn of object,
// and that the angle wraps continuously instead of jumping ±2π mid-drag.

const TAU = Math.PI * 2

/** Right-handed basis for a plane with unit normal `n`: two unit vectors
 *  perpendicular to n and to each other. Picks the reference axis away from n
 *  so the cross product can't collapse. */
export function planeBasis(n) {
  // Normalize defensively: callers pass an axis that is only APPROXIMATELY unit
  // (a rotated basis vector, a hand-written [0.577, 0.577, 0.577]), and `v`
  // below inherits the normal's length, so an un-normalized input yields a
  // basis that is subtly not orthonormal.
  const k = normalize(n)
  // Anything not parallel to k. Comparing |k.x| keeps the choice stable.
  const ref = Math.abs(k[0]) < 0.9 ? [1, 0, 0] : [0, 0, 1]
  const u = normalize(cross(ref, k))
  const v = cross(k, u)
  return { u, v }
}

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2] }
function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]] }

function normalize(v) {
  const l = Math.hypot(v[0], v[1], v[2])
  return l > 0 ? [v[0] / l, v[1] / l, v[2] / l] : [0, 0, 0]
}

/**
 * Intersect a ray with the rotation plane.
 *
 * Returns null when the ray is parallel to the plane (the ring seen exactly
 * edge-on): there is no meaningful angle there, and the caller must keep the
 * previous one rather than snapping the object to a garbage value.
 *
 * @param {number[]} origin  ray origin (camera position)
 * @param {number[]} dir     ray direction, need not be normalized
 * @param {number[]} point   a point on the plane (the gizmo centre)
 * @param {number[]} normal  plane unit normal (the rotation axis, in world space)
 */
export function rayPlaneHit(origin, dir, point, normal) {
  const denom = dot(dir, normal)
  // 1e-6 rather than 0: a near-parallel ray produces a hit thousands of units
  // away, which reads as a wild angle jump.
  if (Math.abs(denom) < 1e-6) return null
  const t = dot(sub(point, origin), normal) / denom
  if (!Number.isFinite(t)) return null
  return [origin[0] + dir[0] * t, origin[1] + dir[1] * t, origin[2] + dir[2] * t]
}

/**
 * The angle of `worldPoint` around `centre`, measured in the plane whose normal
 * is `axis`. Range (-π, π].
 */
export function arcAngle(worldPoint, centre, axis) {
  const n = normalize(axis)
  const { u, v } = planeBasis(n)
  const d = sub(worldPoint, centre)
  return Math.atan2(dot(d, v), dot(d, u))
}

/**
 * Shortest signed difference between two angles, in (-π, π].
 *
 * Load-bearing: atan2 wraps at ±π, so a drag crossing that seam produces a
 * raw difference of nearly 2π — the object would spin a full turn backwards in
 * one frame. Taking the shortest arc keeps the motion continuous.
 */
export function angleDelta(to, from) {
  let d = (to - from) % TAU
  if (d > Math.PI) d -= TAU
  if (d <= -Math.PI) d += TAU
  return d
}

/**
 * Accumulate a drag's total rotation across frames.
 *
 * Returns the running total plus the new "previous" angle to feed the next
 * call. Accumulating per-frame shortest deltas — rather than differencing
 * against the start angle — is what lets a drag exceed 180° and keep going in
 * the same direction instead of folding back.
 *
 * @returns {{total: number, prev: number}}
 */
export function accumulate(total, prevAngle, currentAngle) {
  return { total: total + angleDelta(currentAngle, prevAngle), prev: currentAngle }
}

/** Round to a snap increment (radians). 0/undefined → unsnapped. */
export function snapAngle(angle, increment) {
  if (!increment || !Number.isFinite(increment)) return angle
  return Math.round(angle / increment) * increment
}

/** Degrees, for the on-screen readout PlayCanvas shows while dragging. */
export function toDegrees(rad) {
  return rad * 180 / Math.PI
}
